/**
 * Simulation Content Generator
 * ---------------------------------------------------------------
 * Bridges the simulation engine and the social/app content layer.
 *
 * Both in-app previews and outbound social content (TikTok, IG,
 * YouTube Shorts) are produced from the SAME dialogue engine —
 * scenarios, archetypes, escalation paths, replay logic.
 *
 * Output is a structured JSON object designed for Remotion
 * rendering: every dialogue turn carries timing, role, and a
 * `spiral` flag for cinematic emphasis. CTA screens and captions
 * are produced inline.
 *
 * Pure TypeScript. No network. The optional edge function
 * `generate-simulation-content` can post-process this object with
 * Lovable AI to refine hooks, captions and hashtags — but the
 * deterministic version below is always usable on its own.
 */

import {
  ARCHETYPES,
  type ArchetypeKey,
  type ToneVariant,
} from "./simulationEngine";
import {
  SCENARIOS,
  SCENARIO_CATEGORIES,
  pickScenario,
  pickOpening,
  pickEscalationLine,
  pickTrapLine,
  type EscalationPath,
  type Scenario,
  type ScenarioCategoryKey,
} from "./scenarioLibrary";

// ---------- Types ----------

export type ContentFormat = "spiral" | "replay" | "pov";

export type DialogueRole = "them" | "you" | "you_alt";

export interface DialogueTurn {
  /** Sequence index, 0-based. */
  index: number;
  role: DialogueRole;
  text: string;
  /** Suggested on-screen duration in ms (Remotion-friendly). */
  durationMs: number;
  /** Cinematic pause AFTER this turn, in ms. */
  pauseAfterMs: number;
  /** True if this is the spiral / turning-point beat. */
  spiral?: boolean;
  /** Optional sub-label for split-frame replay clips. */
  track?: "primary" | "alternative";
}

export interface CTAScreen {
  /** Headline shown on the final card. */
  headline: string;
  /** Sub-line / micro-copy. */
  subline: string;
  /** Display brand. */
  brand: string;
  /** Suggested duration in ms. */
  durationMs: number;
}

export interface RemotionTimeline {
  /** Total runtime in ms (sum of turn durations + pauses + CTA). */
  totalMs: number;
  /** Frame rate suggestion. */
  fps: 30;
  /** Aspect ratio suggestion. */
  aspect: "9:16";
  /** Ordered scenes. */
  turns: DialogueTurn[];
  cta: CTAScreen;
}

export interface SimulationContent {
  // Identity
  id: string;
  format: ContentFormat;
  scenarioId: string;
  scenarioTitle: string;
  category: ScenarioCategoryKey;
  categoryLabel: string;
  archetype: ArchetypeKey;
  archetypeLabel: string;

  // Narrative spine
  hook: string;
  dialogue: DialogueTurn[];
  spiralMoment: {
    /** Index in `dialogue` of the bubble that should pulse / glow. */
    turnIndex: number;
    /** What broke — what the spiral represents. */
    note: string;
  };
  betterResponse: string;
  tacticalInsight: string;

  // Distribution
  cta: string;
  caption: string;
  hashtags: string[];

  // Render-ready
  remotion: RemotionTimeline;
}

export interface GenerateOptions {
  format: ContentFormat;
  scenario?: Scenario;
  scenarioId?: string;
  category?: ScenarioCategoryKey;
  toneVariant?: ToneVariant;
}

// ---------- Constants ----------

const DEFAULT_CTA = "Practice the conversation before it happens.";
const BRAND = "GreyRock AI®";

const CORE_HASHTAGS = [
  "#GreyRockAI",
  "#emotionalpressure",
  "#manipulation",
  "#boundaries",
  "#namedthepattern",
];

const CATEGORY_HASHTAGS: Partial<Record<ScenarioCategoryKey, string[]>> = {
  relationships: ["#relationships", "#toxicrelationships"],
  dating: ["#datingadvice", "#redflags"],
  family: ["#familydynamics", "#parentification"],
  friendships: ["#friendshipadvice"],
  workplace: ["#workplaceabuse", "#corporatemanipulation"],
  breakups: ["#breakuprecovery"],
  passive_aggressive: ["#passiveaggressive"],
  breadcrumbing: ["#breadcrumbing"],
  future_faking: ["#futurefaking"],
  fake_apologies: ["#fakeapology", "#nonapology"],
};

// ---------- Hook + Insight templates ----------

const HOOKS_BY_FORMAT: Record<ContentFormat, (s: Scenario) => string[]> = {
  spiral: (s) => [
    `POV: ${s.title.toLowerCase()}.`,
    `Watch how fast this spirals.`,
    `This is the exact moment you lose the room.`,
  ],
  replay: (s) => [
    `Two responses. Two completely different outcomes.`,
    `One reply keeps you trapped. The other ends it.`,
    `Same line. Watch what happens when you don't bite.`,
  ],
  pov: (s) => [
    `POV: you're the one being talked to.`,
    `You haven't said a word. They're already escalating.`,
    `This is what one-sided pressure actually sounds like.`,
  ],
};

const INSIGHTS_BY_ARCHETYPE: Record<ArchetypeKey, string> = {
  invalidation:
    "Invalidation isn't a debate — it's a frame. The moment you defend the feeling, the frame holds.",
  blame_shift:
    "Blame shift survives on your urge to set the record straight. Don't take the bait.",
  deflection:
    "Deflection wins by changing the subject. Stay on yours, even if the silence stings.",
  guilt_pressure:
    "Guilt pressure makes refusal feel cruel. That's the whole tactic. Cruel is not the same as wrong.",
};

const BETTER_RESPONSE_BY_ARCHETYPE: Record<ArchetypeKey, string> = {
  invalidation:
    "I'm not going to debate whether my feeling is valid. I told you what hurt. We can talk when you're ready.",
  blame_shift:
    "We can talk about that separately. Right now I'm telling you about THIS.",
  deflection:
    "I'm staying on this topic. We can discuss the other thing after.",
  guilt_pressure:
    "I'm not responsible for managing your feelings about my limit. The answer is still no.",
};

// ---------- Helpers ----------

function rid(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function pickFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function categoryLabel(key: ScenarioCategoryKey): string {
  return SCENARIO_CATEGORIES.find((c) => c.key === key)?.label ?? key;
}

function buildHashtags(s: Scenario): string[] {
  const cat = CATEGORY_HASHTAGS[s.category] ?? [];
  const seen = new Set<string>();
  return [...CORE_HASHTAGS, ...cat]
    .filter((h) => (seen.has(h) ? false : (seen.add(h), true)))
    .slice(0, 7);
}

function buildCaption(s: Scenario, format: ContentFormat): string {
  const verb =
    format === "replay"
      ? "Watch the same line land two ways."
      : format === "pov"
        ? "One-sided. No interruptions. Just pressure."
        : "Watch how fast it spirals.";
  return `${s.title}. ${verb} Practice the conversation before it happens — ${BRAND}.`;
}

// ---------- Dialogue construction (per format) ----------

function buildSpiralDialogue(s: Scenario): { turns: DialogueTurn[]; spiralIndex: number } {
  const opening = pickOpening(s).line;
  const escalation = pickEscalationLine(s, "emotional_reaction");
  const trap = pickTrapLine(s);

  const turns: DialogueTurn[] = [
    mkTurn(0, "them", opening, false),
    mkTurn(1, "you", "Why are you twisting this? I just said it hurt.", false),
    mkTurn(2, "them", escalation.line, false),
    mkTurn(3, "you", "I never said that. Stop doing this.", true),
    mkTurn(4, "them", trap, false),
  ];
  return { turns, spiralIndex: 3 };
}

function buildReplayDialogue(s: Scenario): { turns: DialogueTurn[]; spiralIndex: number } {
  const opening = pickOpening(s).line;
  const badEscalation = pickEscalationLine(s, "emotional_reaction").line;
  const calmEscalation = pickEscalationLine(s, "controlled").line;

  const turns: DialogueTurn[] = [
    mkTurn(0, "them", opening, false),
    // Bad path
    { ...mkTurn(1, "you", "Are you serious right now? You ALWAYS do this.", true), track: "primary" },
    { ...mkTurn(2, "them", badEscalation, false), track: "primary" },
    // Alt path
    { ...mkTurn(3, "you_alt", "I'm not going to debate that. I told you what I needed.", false), track: "alternative" },
    { ...mkTurn(4, "them", calmEscalation, false), track: "alternative" },
  ];
  return { turns, spiralIndex: 1 };
}

function buildPovDialogue(s: Scenario): { turns: DialogueTurn[]; spiralIndex: number } {
  const opening = pickOpening(s).line;
  const beat2 = pickEscalationLine(s, "avoidant").line;
  const beat3 = pickEscalationLine(s, "over_explaining").line;
  const beat4 = pickTrapLine(s);

  const turns: DialogueTurn[] = [
    mkTurn(0, "them", opening, false),
    mkTurn(1, "them", beat2, false),
    mkTurn(2, "them", beat3, true),
    mkTurn(3, "them", beat4, false),
  ];
  return { turns, spiralIndex: 2 };
}

function mkTurn(index: number, role: DialogueRole, text: string, spiral: boolean): DialogueTurn {
  // Reading-pace heuristic: ~55ms/char, clamped, with extra weight on spiral beats.
  const base = Math.max(1800, Math.min(4200, text.length * 55));
  const durationMs = spiral ? base + 600 : base;
  const pauseAfterMs = spiral ? 900 : 350;
  return { index, role, text, durationMs, pauseAfterMs, spiral: spiral || undefined };
}

// ---------- Public API ----------

export function generateSimulationContent(opts: GenerateOptions): SimulationContent {
  const scenario =
    opts.scenario ??
    (opts.scenarioId
      ? SCENARIOS.find((s) => s.id === opts.scenarioId) ?? pickScenario(opts.category)
      : pickScenario(opts.category));

  const archetype = ARCHETYPES[scenario.archetype];

  const built =
    opts.format === "spiral"
      ? buildSpiralDialogue(scenario)
      : opts.format === "replay"
        ? buildReplayDialogue(scenario)
        : buildPovDialogue(scenario);

  const hook = pickFrom(HOOKS_BY_FORMAT[opts.format](scenario));
  const tacticalInsight = INSIGHTS_BY_ARCHETYPE[scenario.archetype];
  const betterResponse = BETTER_RESPONSE_BY_ARCHETYPE[scenario.archetype];

  const cta: CTAScreen = {
    headline: DEFAULT_CTA,
    subline: "GreyRockAI.app — forensic communication training.",
    brand: BRAND,
    durationMs: 2600,
  };

  const totalMs =
    built.turns.reduce((acc, t) => acc + t.durationMs + t.pauseAfterMs, 0) +
    cta.durationMs;

  const remotion: RemotionTimeline = {
    totalMs,
    fps: 30,
    aspect: "9:16",
    turns: built.turns,
    cta,
  };

  return {
    id: rid("sim"),
    format: opts.format,
    scenarioId: scenario.id,
    scenarioTitle: scenario.title,
    category: scenario.category,
    categoryLabel: categoryLabel(scenario.category),
    archetype: scenario.archetype,
    archetypeLabel: archetype.label,

    hook,
    dialogue: built.turns,
    spiralMoment: {
      turnIndex: built.spiralIndex,
      note:
        opts.format === "replay"
          ? "The reactive reply is the moment the loop locks in."
          : opts.format === "pov"
            ? "By this beat the pressure has compounded — they haven't paused once."
            : "This is where you lose frame. The trap line lands right after.",
    },
    betterResponse,
    tacticalInsight,

    cta: DEFAULT_CTA,
    caption: buildCaption(scenario, opts.format),
    hashtags: buildHashtags(scenario),

    remotion,
  };
}

/** Convenience: produce all three formats for a scenario. */
export function generateContentBundle(scenarioId?: string): SimulationContent[] {
  const scenario = scenarioId
    ? SCENARIOS.find((s) => s.id === scenarioId) ?? pickScenario()
    : pickScenario();
  return (["spiral", "replay", "pov"] as ContentFormat[]).map((format) =>
    generateSimulationContent({ format, scenario })
  );
}

/**
 * Generate a bundle AND persist each format into the telemetry
 * tables (content_assets + zeroed content_metrics). Returns the
 * registered assets so callers get both the payload and the DB ids.
 *
 * Lives here as a thin convenience — all DB work is delegated to
 * src/lib/contentTelemetry.ts to keep that boundary clean.
 */
export async function generateAndRegisterContentBundle(
  scenarioId?: string,
  opts?: { platform?: string }
) {
  const { registerGeneratedBundle } = await import("./contentTelemetry");
  const bundle = generateContentBundle(scenarioId);
  const registered = await registerGeneratedBundle(bundle, opts ?? {});
  return { bundle, registered, assetIds: registered.map((r) => r.assetId) };
}
